/*
 * Glossary terms. Scans text-heavy areas for a list of terms, marks each
 * one as a soft pill, and shows a definition card on hover, tap, click or
 * keyboard focus. Terms come from the shop's glossary_term metaobjects when
 * they exist, otherwise from assets/microdose-glossary-terms.json.
 *
 * Rendered by snippets/microdose-glossary.liquid; settings under
 * Theme settings → Glossary. Never touches links, headings, buttons,
 * citation superscripts or the audio player's own elements, and never
 * changes the text itself — only wraps it — so the narration player's
 * page-hash check is unaffected.
 *
 *   window.MicrodoseGlossary.scan(rootElement)   mark terms in content added later
 */
(function () {
  'use strict';

  var hook = document.querySelector('[data-md-glossary]');
  if (!hook) return;

  var SKIP = 'a, button, [role="button"], h1, h2, h3, h4, h5, h6, sup, code, pre, kbd, script, style, textarea, input, select, label, .md-term, .md-term-pop, .md-audio, .md-audio-overlay, [data-md-glossary-skip]';
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  var config = {
    selectors: hook.getAttribute('data-selectors') || '[data-md-content]',
    mode: hook.getAttribute('data-mode') || 'first',
    linkLabel: hook.getAttribute('data-link-label') || 'Open glossary',
    moreLabel: hook.getAttribute('data-more-label') || 'Read more',
    lessLabel: hook.getAttribute('data-less-label') || 'Show less',
    termsUrl: hook.getAttribute('data-terms-url') || '',
    useFile: hook.getAttribute('data-use-file') || 'fallback'   /* 'fallback' | 'always' */
  };

  var terms = [];
  var byKey = {};
  var matcher = null;

  /* ---------- terms ---------- */
  /* Dashboard entries (glossary_term metaobjects, inlined by the snippet) are
     the source. The starter file is a fallback while there are none, or is
     merged in (dashboard wins on the same term) when the theme setting
     "Also use the built-in starter list" is on. */
  function loadTerms() {
    var fromAdmin = [];
    var inline = hook.querySelector('script[type="application/json"]');
    if (inline) {
      try { fromAdmin = JSON.parse(inline.textContent) || []; } catch (e) { fromAdmin = []; }
    }
    if (fromAdmin.length && config.useFile !== 'always') return Promise.resolve(fromAdmin);
    var fromFile = config.termsUrl
      ? fetch(config.termsUrl).then(function (r) { return r.ok ? r.json() : []; }).catch(function () { return []; })
      : Promise.resolve([]);
    return fromFile.then(function (list) {
      var taken = {};
      fromAdmin.forEach(function (t) { if (t && t.term) taken[String(t.term).trim().toLowerCase()] = true; });
      var rest = (list || []).filter(function (t) { return t && t.term && !taken[String(t.term).trim().toLowerCase()]; });
      return fromAdmin.concat(rest);
    });
  }

  function escapeRe(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function build(list) {
    terms = [];
    byKey = {};
    var forms = [];
    list.forEach(function (t, i) {
      if (!t || !t.term || !t.definition) return;
      var key = 't' + i;
      var entry = {
        key: key,
        term: String(t.term).trim(),
        definition: t.definition,
        definitionLong: t.definition_long || t.definitionLong || '',
        link: t.link || '',
        linkLabel: t.link_label || t.linkLabel || ''
      };
      byKey[key] = entry;
      terms.push(entry);
      var all = [entry.term].concat(t.aliases || []);
      all.forEach(function (f) {
        f = String(f).trim();
        if (!f) return;
        forms.push({ form: f, key: key, acronym: /^[A-Z0-9][A-Z0-9-]{1,7}$/.test(f) });
      });
    });
    /* Longest first so "beta-caryophyllene" wins over "caryophyllene" */
    forms.sort(function (a, b) { return b.form.length - a.form.length; });
    if (!forms.length) { matcher = null; return; }
    var alt = forms.map(function (f) { return escapeRe(f.form).replace(/\\?[-‑\s]+/g, '[\\s\\-\\u2011]+'); }).join('|');
    /* Word boundaries that also treat hyphens and apostrophes as part of a word */
    matcher = { re: new RegExp('(^|[^\\w\\-\\u2019\'])(' + alt + ')(?![\\w\\-\\u2019\'])', 'gi'), forms: forms };
  }

  function keyFor(text) {
    var flat = text.replace(/[\s\-‑]+/g, ' ').toLowerCase();
    for (var i = 0; i < matcher.forms.length; i++) {
      var f = matcher.forms[i];
      if (f.form.replace(/[\s\-‑]+/g, ' ').toLowerCase() === flat) {
        /* An acronym only counts when written as one ("CBD", not "cbd") */
        if (f.acronym && text !== f.form) return null;
        return f.key;
      }
    }
    return null;
  }

  /* ---------- marking ---------- */
  function scan(root) {
    if (!matcher || !root) return 0;
    var containers = root.matches && root.matches(config.selectors) ? [root] : Array.prototype.slice.call(root.querySelectorAll(config.selectors));
    var count = 0;
    containers.forEach(function (c) {
      if (c.__mdGlossaryDone) return;
      c.__mdGlossaryDone = true;
      var seen = {};
      var walker = document.createTreeWalker(c, NodeFilter.SHOW_TEXT, {
        acceptNode: function (node) {
          if (!node.nodeValue || !/\S/.test(node.nodeValue)) return NodeFilter.FILTER_REJECT;
          var el = node.parentElement;
          if (el && el.closest && el.closest(SKIP)) return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        }
      });
      var nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      nodes.forEach(function (node) {
        var text = node.nodeValue;
        matcher.re.lastIndex = 0;
        var m, last = 0, frag = null;
        while ((m = matcher.re.exec(text))) {
          var key = keyFor(m[2]);
          if (!key || (config.mode === 'first' && seen[key])) continue;
          seen[key] = true;
          if (!frag) frag = document.createDocumentFragment();
          var start = m.index + m[1].length;
          frag.appendChild(document.createTextNode(text.slice(last, start)));
          var span = document.createElement('span');
          span.className = 'md-term';
          span.setAttribute('role', 'button');
          span.setAttribute('tabindex', '0');
          span.setAttribute('aria-expanded', 'false');
          span.setAttribute('aria-haspopup', 'dialog');
          span.setAttribute('data-term', key);
          span.textContent = m[2];
          frag.appendChild(span);
          last = start + m[2].length;
          count++;
        }
        if (frag) {
          frag.appendChild(document.createTextNode(text.slice(last)));
          node.parentNode.replaceChild(frag, node);
        }
      });
    });
    return count;
  }

  /* ---------- the card ---------- */
  var pop = null;
  var current = null;   /* the .md-term the card belongs to */
  var pinned = false;   /* opened by click / tap / keyboard: stays until dismissed */
  var showTimer = null, hideTimer = null;

  function ensurePop() {
    if (pop) return pop;
    pop = document.createElement('div');
    pop.className = 'md-term-pop';
    pop.setAttribute('role', 'dialog');
    pop.setAttribute('aria-live', 'polite');
    pop.hidden = true;
    pop.innerHTML =
      '<div class="md-term-pop__arrow" aria-hidden="true"></div>' +
      '<div class="md-term-pop__inner">' +
        '<p class="md-term-pop__term"></p>' +
        '<div class="md-term-pop__def"></div>' +
        '<div class="md-term-pop__long" hidden></div>' +
        '<div class="md-term-pop__actions">' +
          '<button type="button" class="md-term-pop__more" aria-expanded="false" hidden>' +
            '<span class="md-term-pop__more-text"></span>' +
            '<svg class="md-term-pop__more-icon" width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 3.5l3 3 3-3"/></svg>' +
          '</button>' +
          '<a class="md-term-pop__link" hidden></a>' +
        '</div>' +
        '<button type="button" class="md-term-pop__close" aria-label="Close">' +
          '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" aria-hidden="true"><path d="M3 3l8 8M11 3l-8 8"/></svg>' +
        '</button>' +
      '</div>';
    document.body.appendChild(pop);
    pop.addEventListener('mouseenter', function () { clearTimeout(hideTimer); });
    pop.addEventListener('mouseleave', function () { if (!pinned) scheduleHide(); });
    pop.querySelector('.md-term-pop__close').addEventListener('click', function () { hide(true); });
    pop.querySelector('.md-term-pop__more').addEventListener('click', function () {
      /* Reading the long version is a deliberate act: pin the card so it
         survives the mouse leaving, then grow it in place. */
      pinned = true;
      pop.classList.add('is-pinned');
      setExpanded(!pop.classList.contains('is-expanded'));
      position();
    });
    return pop;
  }

  function setHTML(el, html) {
    if (/<[a-z][\s\S]*>/i.test(html)) el.innerHTML = html; else el.textContent = html;
  }

  function setExpanded(on) {
    if (!pop) return;
    var long = pop.querySelector('.md-term-pop__long');
    var btn = pop.querySelector('.md-term-pop__more');
    var def = pop.querySelector('.md-term-pop__def');
    pop.classList.toggle('is-expanded', on);
    long.hidden = !on;
    /* The long version restates the short one, so swap rather than stack */
    def.hidden = on && long.getAttribute('data-restates') === 'true';
    btn.setAttribute('aria-expanded', on ? 'true' : 'false');
    btn.querySelector('.md-term-pop__more-text').textContent = on ? config.lessLabel : config.moreLabel;
  }

  function fill(entry) {
    var p = ensurePop();
    p.querySelector('.md-term-pop__term').textContent = entry.term;
    var def = p.querySelector('.md-term-pop__def');
    setHTML(def, entry.definition);
    var long = p.querySelector('.md-term-pop__long');
    var btn = p.querySelector('.md-term-pop__more');
    if (entry.definitionLong) {
      setHTML(long, entry.definitionLong);
      /* If the long text opens with the short text, show only the long one when expanded */
      var shortFlat = def.textContent.replace(/\s+/g, ' ').trim().slice(0, 60);
      var longFlat = long.textContent.replace(/\s+/g, ' ').trim();
      long.setAttribute('data-restates', shortFlat && longFlat.indexOf(shortFlat) === 0 ? 'true' : 'false');
      btn.hidden = false;
    } else {
      long.innerHTML = '';
      btn.hidden = true;
    }
    setExpanded(false);
    var a = p.querySelector('.md-term-pop__link');
    if (entry.link) {
      a.href = entry.link;
      var label = entry.linkLabel || config.linkLabel;
      if (entry.definitionLong && label.toLowerCase() === config.moreLabel.toLowerCase()) label = 'Open glossary';
      a.textContent = label;
      a.hidden = false;
    } else {
      a.hidden = true;
    }
  }

  function position() {
    if (!current || !pop) return;
    var r = current.getBoundingClientRect();
    var margin = 12;
    var vw = document.documentElement.clientWidth;
    var vh = window.innerHeight;
    pop.style.maxWidth = Math.min(pop.classList.contains('is-expanded') ? 380 : 340, vw - margin * 2) + 'px';
    var pw = pop.offsetWidth;
    var ph = pop.offsetHeight;
    var headerH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 64;
    var below = r.bottom + 10 + ph <= vh - margin || r.top - 10 - ph < headerH + margin;
    var top = below ? r.bottom + 10 : r.top - 10 - ph;
    var left = r.left + r.width / 2 - pw / 2;
    left = Math.max(margin, Math.min(left, vw - margin - pw));
    pop.style.transform = 'translate(' + Math.round(left + window.pageXOffset) + 'px,' + Math.round(top + window.pageYOffset) + 'px)';
    pop.classList.toggle('is-below', below);
    pop.classList.toggle('is-above', !below);
    var arrow = pop.querySelector('.md-term-pop__arrow');
    arrow.style.left = Math.round(Math.max(16, Math.min(pw - 16, r.left + r.width / 2 - left))) + 'px';
  }

  function show(term, pin) {
    var entry = byKey[term.getAttribute('data-term')];
    if (!entry) return;
    clearTimeout(hideTimer);
    if (current && current !== term) current.setAttribute('aria-expanded', 'false');
    var sameTerm = current === term;
    current = term;
    pinned = !!pin || (pinned && sameTerm);
    /* Re-hovering the open term keeps its expanded state; a new term starts short */
    if (!sameTerm || pop.hidden) fill(entry);
    pop.classList.toggle('is-pinned', pinned);
    pop.hidden = false;
    term.setAttribute('aria-expanded', 'true');
    term.classList.add('is-open');
    position();
    requestAnimationFrame(function () { pop.classList.add('is-on'); });
  }

  function hide(force) {
    if (!pop || pop.hidden) return;
    if (pinned && !force) return;
    pinned = false;
    pop.classList.remove('is-on', 'is-pinned');
    if (current) { current.setAttribute('aria-expanded', 'false'); current.classList.remove('is-open'); }
    var el = pop;
    var done = function () { if (!el.classList.contains('is-on')) el.hidden = true; };
    if (reduceMotion) done(); else setTimeout(done, 180);
    current = null;
  }

  function scheduleHide() {
    clearTimeout(hideTimer);
    hideTimer = setTimeout(function () { hide(false); }, 220);
  }

  function bind() {
    document.addEventListener('mouseover', function (e) {
      if (!finePointer) return;
      var term = e.target.closest && e.target.closest('.md-term');
      if (!term) return;
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
      if (current === term) return;
      showTimer = setTimeout(function () { if (!pinned) show(term, false); }, 140);
    });
    document.addEventListener('mouseout', function (e) {
      var term = e.target.closest && e.target.closest('.md-term');
      if (!term) return;
      clearTimeout(showTimer);
      if (!pinned) scheduleHide();
    });
    document.addEventListener('click', function (e) {
      var term = e.target.closest && e.target.closest('.md-term');
      if (term) {
        e.preventDefault();
        if (current === term && pinned) { hide(true); return; }
        show(term, true);
        return;
      }
      if (pop && !pop.hidden && !(e.target.closest && e.target.closest('.md-term-pop'))) hide(true);
    });
    document.addEventListener('keydown', function (e) {
      var term = e.target.closest && e.target.closest('.md-term');
      if (term && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        if (current === term && pinned) hide(true); else show(term, true);
        return;
      }
      if (e.key === 'Escape' && pop && !pop.hidden) {
        var back = current;
        hide(true);
        if (back) back.focus();
      }
    });
    document.addEventListener('focusin', function (e) {
      var term = e.target.closest && e.target.closest('.md-term');
      if (term && current !== term) show(term, false);
    });
    document.addEventListener('focusout', function (e) {
      var term = e.target.closest && e.target.closest('.md-term');
      if (term && !pinned) scheduleHide();
    });
    window.addEventListener('scroll', function () { if (current) position(); }, { passive: true });
    window.addEventListener('resize', function () { if (current) position(); });
  }

  loadTerms().then(function (list) {
    build(list);
    if (!matcher) return;
    var n = scan(document);
    if (n) bind();
    window.MicrodoseGlossary = { scan: function (root) { var k = scan(root || document); if (k && !pop) bind(); return k; }, terms: terms };
  });
})();
